import { useState } from 'react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useLang } from '../i18n/LanguageContext';
import styles from './Contact.module.css';

// The form submits to Formspree. The destination email is configured privately
// inside the Formspree dashboard — it never appears in this repo or in the page HTML,
// so bots can't scrape it. To activate:
//   1. Create a form at https://formspree.io and set its destination to your email.
//   2. Copy the form ID from the generated endpoint (e.g. .../f/abcdwxyz).
//   3. Replace YOUR_FORM_ID below. No other change needed.

export default function Contact() {
  const revealRef = useScrollReveal();
  const { t, tr } = useLang();
  const [status, setStatus] = useState('idle');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');

    const form = e.target;
    const data = new FormData(form);

    try {
      const res = await fetch(form.action, {
        method: 'POST',
        body: data,
        headers: { Accept: 'application/json' },
      });

      if (res.ok) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="contact" className={styles.section}>
      <div className={styles.glow} aria-hidden="true" />
      <div className={`container reveal ${styles.inner}`} ref={revealRef}>
        <p className="section-label">{t('contact.eyebrow')}</p>
        <h2 className={styles.headline}>{tr("Let's work together.", 'Lassen Sie uns zusammenarbeiten.')}</h2>
        <p className={styles.subtext}>
          {tr(
            <>
              Whether you&apos;re planning a congress, running a summit, or need a team that can keep up
              with a live event, we&apos;d love to hear from you.
            </>,
            <>
              Ob Sie einen Kongress planen, einen Summit durchführen oder ein Team brauchen, das mit
              einem Live-Event mithalten kann — wir freuen uns, von Ihnen zu hören.
            </>,
          )}
        </p>

        <ul className={styles.trust} aria-label={tr('What to expect', 'Was Sie erwartet')}>
          <li>{tr('Replies within 24 hours, Mon-Fri', 'Antwort innerhalb von 24 Stunden, Mo–Fr')}</li>
          <li>{tr('Berlin-based, filming across Europe', 'Berlin-basiert, drehend in ganz Europa')}</li>
          <li>
            {tr('or call', 'oder anrufen')}{' '}
            <a className={styles.phone} href="tel:+4915232782970">
              +49 1523 2782970
            </a>
          </li>
        </ul>

        <form
          className={styles.form}
          action="https://formspree.io/f/xkolyzpl"
          method="POST"
          onSubmit={handleSubmit}
        >
          <div className={styles.field}>
            <label htmlFor="name">{t('contact.name')}</label>
            <input type="text" id="name" name="name" required autoComplete="name" />
          </div>

          <div className={styles.field}>
            <label htmlFor="company">{t('contact.company')}</label>
            <input type="text" id="company" name="company" autoComplete="organization" />
          </div>

          <div className={styles.field}>
            <label htmlFor="email">{t('contact.email')}</label>
            <input type="email" id="email" name="email" required autoComplete="email" />
          </div>

          <div className={styles.field}>
            <label htmlFor="message">{t('contact.message')}</label>
            <textarea id="message" name="message" rows="5" required />
          </div>

          <button type="submit" className="btn-gradient" disabled={status === 'sending'}>
            {status === 'sending' ? t('contact.sending') : t('contact.send')}
          </button>

          {status === 'success' && (
            <p className={styles.feedback} role="status">
              {tr("Thank you. We'll be in touch soon.", 'Danke. Wir melden uns in Kürze.')}
            </p>
          )}
          {status === 'error' && (
            <p className={`${styles.feedback} ${styles.feedbackError}`} role="alert">
              {tr(
                'Something went wrong. Please try again, or reach us through the form above.',
                'Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut oder erreichen Sie uns über das Formular oben.',
              )}
            </p>
          )}
        </form>
      </div>
    </section>
  );
}
